import { useEffect } from "react";
import { TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import type { StyleSpecification } from "maplibre-gl";
import "@maplibre/maplibre-gl-leaflet";
import "maplibre-gl/dist/maplibre-gl.css";

import { mapHiddenSourceLayers, mapShowRoads, mapStyleUrl, mapTileAttribution, mapTileUrl } from "../../config";

/**
 * The app's base map. By default renders OpenFreeMap vector tiles through MapLibre as a Leaflet layer, so all the
 * existing Leaflet overlays (GeoJSON, circles, markers, heat layers) keep working unchanged. If a raster
 * REACT_APP_MAP_TILE_URL is configured that is used instead (see config.ts).
 */
export default function MapTileLayer() {
  if (mapTileUrl) {
    return (
      <TileLayer
        url={mapTileUrl}
        attribution={mapTileAttribution}
        maxZoom={19}
        referrerPolicy="strict-origin-when-cross-origin"
        detectRetina={false}
      />
    );
  }
  return <VectorBaseLayer />;
}

let stylePromise: Promise<StyleSpecification | string> | null = null;

/**
 * Resolve the style to hand to MapLibre. With roads hidden we fetch the style JSON once and drop the layers we don't
 * want (roads, buildings, airports…) so only land, water, boundaries and place labels remain. Falls back to the
 * plain URL if the fetch fails so the map still renders.
 */
function loadStyle(): Promise<StyleSpecification | string> {
  if (mapShowRoads) return Promise.resolve(mapStyleUrl);
  if (!stylePromise) {
    stylePromise = fetch(mapStyleUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`Map style ${r.status}`);
        return r.json() as Promise<StyleSpecification>;
      })
      .then((style) => ({
        ...style,
        layers: style.layers.filter(
          (l) => !("source-layer" in l && l["source-layer"] && mapHiddenSourceLayers.includes(l["source-layer"])),
        ),
      }))
      .catch((err) => {
        console.warn("Falling back to full map style", err);
        return mapStyleUrl;
      });
  }
  return stylePromise;
}

function VectorBaseLayer() {
  const map = useMap();

  useEffect(() => {
    let layer: L.MaplibreGL | null = null;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // MapLibre sizes its canvas on creation; make sure it matches once Leaflet has settled its container.
    const resize = () => {
      try {
        layer?.getMaplibreMap()?.resize();
      } catch {
        /* map not ready yet */
      }
    };

    loadStyle().then((style) => {
      if (cancelled) return;
      layer = L.maplibreGL({
        style,
        attribution: mapTileAttribution,
        interactive: false,
      } as L.LeafletMaplibreGLOptions);
      layer.addTo(map);
      timer = setTimeout(resize, 100);
      map.on("resize", resize);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      map.off("resize", resize);
      if (layer) map.removeLayer(layer);
    };
  }, [map]);

  return null;
}
